import {
  FC,
  lazy,
  memo,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Skeleton,
  Slider,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Edit from '@mui/icons-material/Edit'
import Fullscreen from '@mui/icons-material/Fullscreen'
import VolumeOff from '@mui/icons-material/VolumeOff'
import VolumeUp from '@mui/icons-material/VolumeUp'
import { useNavigate } from 'react-router-dom'
import QuickAccessLink from './QuickAccessLink'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { useAuth } from '@/hooks/useAuth'
import { canAccessContent } from '@/utils/permissions'
import {
  selectCarouselError,
  selectCarouselItemsWithFixed,
  selectCarouselIsLoading,
  selectCarouselIsSaving,
} from '@/features/carousel/carouselSelectors'
import { fetchCarouselConfig } from '@/features/carousel/carouselThunks'
import { openEditModal } from '@/features/carousel/carouselSlice'
import { useSnackbar } from '@/hooks/useSnackbar'
import MediaPreviewDialog from '@/components/common/MediaPreviewDialog'

const CarouselEditModal = lazy(() => import('./CarouselEditModal'))

const IMAGE_DISPLAY_MS = 10_000

const HeroStaticContent: FC = memo(() => {
  const navigate = useNavigate()
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45)), url(/images/home/home.png)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          padding: '0 1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: { xs: '100%', sm: 600, md: 800, lg: 1100 },
            margin: '0 auto',
            color: '#fff',
            alignItems: 'center',
            mt: { xs: 0, md: 8 },
            width: { xs: '100%', md: '80%', lg: '60%' },
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: '#fff',
              mb: { xs: 2, sm: 3, md: 4 },
              fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem', lg: '2.5rem' },
              textAlign: 'center',
              lineHeight: 1.2,
              textShadow:
                '0 0 10px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 0, 0, 0.4), 2px 2px 8px rgba(0, 0, 0, 0.9), -2px -2px 8px rgba(0, 0, 0, 0.5)',
            }}
          >
            <Box
              component="span"
              sx={{
                color: 'secondary.main',
                fontWeight: 700,
                textShadow:
                  '0 0 10px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 0, 0, 0.4), 2px 2px 8px rgba(0, 0, 0, 0.9), -2px -2px 8px rgba(0, 0, 0, 0.5)',
              }}
            >
              Formando{' '}
            </Box>{' '}
            líderes
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.938rem', sm: '1.125rem', md: '1.25rem' },
              textAlign: 'center',
              textShadow:
                '0 0 10px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 0, 0, 0.4), 2px 2px 8px rgba(0, 0, 0, 0.9), -2px -2px 8px rgba(0, 0, 0, 0.5)',
            }}
          >
            con valores, ciencia y proyección social
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#e6eef8',
              maxWidth: { xs: '100%', sm: 500, md: 700, lg: 800 },
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem' },
              textAlign: 'center',
              lineHeight: { xs: 1.5, md: 1.6 },
              textShadow:
                '0 0 10px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 0, 0, 0.4), 2px 2px 8px rgba(0, 0, 0, 0.9), -2px -2px 8px rgba(0, 0, 0, 0.5)',
            }}
          >
            Somos una institución educativa comprometida con la formación integral de nuestros
            estudiantes desde preescolar hasta educación media.
          </Typography>
          <Button
            onClick={() => navigate('/institutional')}
            sx={{
              backgroundColor: 'primary.dark',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              mt: { xs: 2, sm: 3, md: 4 },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 160, md: 180 },
              px: { xs: 3, sm: 4, md: 5 },
              py: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.938rem', sm: '1rem' },
              '&:hover': { backgroundColor: 'secondary.main' },
            }}
          >
            Conoce Más
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

const HeroCarouselBase: FC = () => {
  const dispatch = useAppDispatch()
  const items = useAppSelector(selectCarouselItemsWithFixed)
  const isLoading = useAppSelector(selectCarouselIsLoading)
  const isSaving = useAppSelector(selectCarouselIsSaving)
  const carouselError = useAppSelector(selectCarouselError)
  const { authUser, userRole } = useAuth()
  const canEdit = canAccessContent(userRole, authUser?.email)
  const snackbar = useSnackbar()

  const [activeIndex, setActiveIndex] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)
  // Por requerimiento de UX el estado inicial es CON sonido. Como los navegadores
  // bloquean autoplay con audio sin gesto previo del usuario, el effect de tryPlay
  // intenta primero con audio y, si el browser rechaza, hace fallback automático a
  // muted=true para que el video al menos se reproduzca. Tras el primer gesto del
  // usuario (click en cualquier control) podrá activar audio sin problema.
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(0.8)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const prevSavingRef = useRef(isSaving)
  const initialPositionedRef = useRef(false)
  const prevLoadingRef = useRef(isLoading)

  useEffect(() => {
    dispatch(fetchCarouselConfig())
  }, [dispatch])

  // Posiciona el carrusel en el ítem fijo "Contenido institucional" la primera vez
  // que el fetch inicial completa (flanco descendente de isLoading). Garantiza que
  // el visitante vea siempre primero el contenido institucional al cargar la página,
  // independientemente de cuántos ítems dinámicos haya. useLayoutEffect evita el
  // parpadeo de un dinámico antes del reposicionamiento. Solo se ejecuta una vez:
  // tras el posicionamiento inicial, el ciclo natural del carrusel sigue su curso.
  useLayoutEffect(() => {
    if (!initialPositionedRef.current && prevLoadingRef.current && !isLoading) {
      const fixedIdx = items.findIndex((it) => it.fixed)
      if (fixedIdx !== -1) setActiveIndex(fixedIdx)
      initialPositionedRef.current = true
    }
    prevLoadingRef.current = isLoading
  }, [isLoading, items])

  // Reset al inicio cuando cambia el set de ítems (p. ej. tras guardar configuración)
  // para evitar que `activeIndex` apunte a un slot inexistente.
  useEffect(() => {
    setActiveIndex((prev) => (prev >= items.length ? 0 : prev))
  }, [items.length])

  // Lógica dual de autoavance:
  //   - Videos: avanzan por el evento `onEnded` del <video> (manejado en handleVideoEnded);
  //     este effect retorna temprano y no programa timer.
  //   - Imágenes y el ítem fijo estático: avanzan tras IMAGE_DISPLAY_MS (10 s) con un
  //     setTimeout cuyo handle se limpia en el cleanup para evitar avances huérfanos
  //     al desmontar o cambiar de ítem.
  useEffect(() => {
    if (items.length <= 1) return
    // Pausa el autoavance mientras el visor expandido está abierto para que el
    // carrusel no rote por detrás del overlay.
    if (previewOpen) return
    const currentItem = items[activeIndex]
    if (!currentItem || currentItem.type === 'video') return
    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % items.length)
    }, IMAGE_DISPLAY_MS)
    return () => clearTimeout(timer)
  }, [activeIndex, items, previewOpen])

  // Snackbar de éxito al guardar — solo en el flanco descendente de isSaving
  // y siempre que no haya error nuevo (la rama rejected del thunk setea `error`,
  // de modo que un fallo no dispara el mensaje de éxito por error).
  useEffect(() => {
    if (prevSavingRef.current && !isSaving && !carouselError) {
      snackbar.show('Carrusel actualizado correctamente', 'success')
    }
    prevSavingRef.current = isSaving
  }, [isSaving, carouselError, snackbar])

  const handleVideoEnded = () => {
    // Si el visor expandido está abierto, ignoramos el avance natural para no
    // descalzar el contexto cuando el usuario cierre el overlay.
    if (previewOpen) return
    setActiveIndex((prev) => (prev + 1) % items.length)
  }

  // Navegación manual con flechas: el useEffect del autoavance observa `activeIndex`
  // como dependencia, por lo que al cambiar manualmente el slot, el timer existente
  // se limpia (cleanup) y se programa uno nuevo desde cero — sin lógica adicional.
  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length)
  }
  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length)
  }

  const handleOpenEditor = () => {
    dispatch(openEditModal())
  }

  const currentItem = useMemo(() => items[activeIndex], [items, activeIndex])
  const currentItemUrl = currentItem?.url
  const currentItemType = currentItem?.type

  // Mantiene el `<video>` sincronizado con `volume`. `muted` ya se propaga vía prop;
  // `volume` no tiene atributo HTML correspondiente, así que se aplica imperativamente.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.volume = volume
  }, [volume])

  // Al cambiar de slot a un video, intenta autoplay con el estado de audio actual.
  // Si el navegador rechaza autoplay con audio (política sin user gesture), hace
  // fallback automático a muted=true y reintenta, garantizando reproducción.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (currentItemType !== 'video') return
    v.volume = volume
    const playPromise = v.play?.()
    if (playPromise === undefined) return
    playPromise.catch(() => {
      v.muted = true
      setMuted(true)
      v.play().catch(() => {})
    })
  }, [currentItemUrl, currentItemType, volume])

  return (
    <Box component="section">
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '45vh', md: '60vh' },
          overflow: 'hidden',
          backgroundColor: '#000',
        }}
      >
        {isLoading && items.length === 0 ? (
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        ) : (
          currentItem && (
            <Fade in timeout={400} key={`${currentItem.mediaId}-${activeIndex}`}>
              <Box sx={{ position: 'absolute', inset: 0 }}>
                {currentItem.fixed ? (
                  <HeroStaticContent />
                ) : currentItem.type === 'video' ? (
                  <Box
                    component="video"
                    ref={videoRef}
                    src={currentItem.url}
                    autoPlay
                    muted={muted}
                    playsInline
                    loop={false}
                    preload="metadata"
                    onEnded={handleVideoEnded}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: currentItem.fitMode ?? 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={currentItem.url}
                    alt={currentItem.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: currentItem.fitMode ?? 'cover',
                      display: 'block',
                    }}
                  />
                )}
              </Box>
            </Fade>
          )
        )}

        {currentItem && !currentItem.fixed && currentItem.url && (
          <Tooltip title="Ver en pantalla completa" arrow placement="right">
            <IconButton
              aria-label="Ver en pantalla completa"
              onClick={() => setPreviewOpen(true)}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 10,
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                color: '#fff',
                backdropFilter: 'blur(4px)',
                transition: 'background-color 200ms ease, transform 200ms ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.65)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {currentItem && !currentItem.fixed && currentItem.type === 'video' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, md: 1 },
              pl: 0.5,
              pr: { xs: 1, md: 1.5 },
              py: 0.25,
              borderRadius: 999,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(4px)',
              transition: 'background-color 200ms ease',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
            }}
          >
            <Tooltip title={muted ? 'Activar sonido' : 'Silenciar'} arrow placement="top">
              <IconButton
                aria-label={muted ? 'Activar sonido' : 'Silenciar'}
                onClick={() => setMuted((m) => !m)}
                sx={{
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  color: '#fff',
                  '&:hover': { transform: 'scale(1.05)' },
                  transition: 'transform 200ms ease',
                }}
              >
                {muted ? (
                  <VolumeOff fontSize="small" />
                ) : (
                  <VolumeUp fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Slider
              size="small"
              aria-label="Volumen"
              value={muted ? 0 : Math.round(volume * 100)}
              min={0}
              max={100}
              onChange={(_e, value) => {
                const v = Array.isArray(value) ? value[0] : value
                const next = v / 100
                setVolume(next)
                if (v === 0) {
                  setMuted(true)
                } else if (muted) {
                  setMuted(false)
                }
              }}
              sx={{
                width: { xs: 60, md: 90 },
                color: '#fff',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  backgroundColor: '#fff',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 6px rgba(255,255,255,0.16)',
                  },
                },
                '& .MuiSlider-track': { border: 'none' },
                '& .MuiSlider-rail': { color: 'rgba(255,255,255,0.4)' },
              }}
            />
          </Box>
        )}

        {canEdit && (
          <Tooltip title="Editar carrusel" arrow placement="left">
            <IconButton
              aria-label="Editar carrusel"
              onClick={handleOpenEditor}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                color: '#fff',
                backdropFilter: 'blur(4px)',
                transition: 'background-color 200ms ease, transform 200ms ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.65)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {items.length > 1 && (
          <>
            <IconButton
              aria-label="Anterior"
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                top: '50%',
                left: { xs: 8, md: 16 },
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                color: '#fff',
                backdropFilter: 'blur(4px)',
                transition: 'background-color 200ms ease, transform 200ms ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.65)',
                  transform: 'translateY(-50%) scale(1.05)',
                },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Siguiente"
              onClick={handleNext}
              sx={{
                position: 'absolute',
                top: '50%',
                right: { xs: 8, md: 16 },
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                color: '#fff',
                backdropFilter: 'blur(4px)',
                transition: 'background-color 200ms ease, transform 200ms ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.65)',
                  transform: 'translateY(-50%) scale(1.05)',
                },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </>
        )}

        {items.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              zIndex: 10,
            }}
          >
            {items.map((item, i) => (
              <Box
                key={`${item.mediaId}-dot-${i}`}
                sx={{
                  width: i === activeIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    i === activeIndex ? 'secondary.main' : 'rgba(255,255,255,0.6)',
                  transition: 'width 200ms ease, background-color 200ms ease',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          background: '#fff',
          borderRadius: { xs: '4px', sm: '6px' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { sm: 'wrap', md: 'nowrap' },
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'space-around' },
          boxShadow: {
            xs: '0 2px 8px rgba(102, 102, 102, 0.2)',
            sm: '0 4px 12px rgba(102, 102, 102, 0.3)',
          },
          gap: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
          width: '100%',
          position: 'relative',
          zIndex: 10,
          padding: { xs: '20px 12px', sm: '25px 16px' },
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          sx={{
            color: '#666',
            fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem' },
            fontWeight: { xs: 500, sm: 400 },
            mb: { xs: 1, sm: 0 },
          }}
        >
          Acceso Rápido
        </Typography>
        <QuickAccessLink
          href="https://app.udeki.com/login"
          imgSrc="images/udeki_logos.png"
          alt="udeki"
        />
        <QuickAccessLink
          href="https://www.office.com/"
          imgSrc="images/office-365-1.svg"
          alt="Office 365"
        />
        <QuickAccessLink
          href="https://classroom.google.com/"
          imgSrc="images/classroom_logo.png"
          alt="Google Classroom"
        />
      </Box>

      {canEdit && (
        <Suspense
          fallback={
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 1300,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <CarouselEditModal />
        </Suspense>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={snackbar.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert onClose={snackbar.close} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <MediaPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        src={currentItem && !currentItem.fixed ? currentItem.url : ''}
        type={currentItem?.type === 'video' ? 'video' : 'image'}
        name={currentItem?.name}
      />
    </Box>
  )
}

const HeroCarousel = memo(HeroCarouselBase)
export default HeroCarousel
